// Per-conversation ephemeral key + nonce sequence management.

import { EPHEMERAL_SECRET_LABEL, EPHEMERAL_SECRET_LENGTH } from './types';
import { wipeBytes } from './crypto';

export interface EphemeralSecretExporter {
  exportConversationSecret(
    conversationId: Uint8Array,
    label: string,
    context: Uint8Array,
    length: number,
  ): Promise<Uint8Array>;
}

interface ConvState {
  epoch: number;
  nonceSeq: number;
  key: Uint8Array | null;
}

export class EphemeralKeyring {
  private readonly states = new Map<string, ConvState>();
  private readonly exporter: EphemeralSecretExporter;

  constructor(exporter: EphemeralSecretExporter) {
    this.exporter = exporter;
  }

  private keyFor(convId: Uint8Array): string {
    return bytesToHex(convId);
  }

  /** Update epoch from MLS; resets nonce_seq when epoch advances. */
  setEpoch(conversationId: Uint8Array, epoch: number): void {
    const k = this.keyFor(conversationId);
    const existing = this.states.get(k);
    if (existing !== undefined && existing.epoch === epoch) {
      return;
    }
    if (existing?.key !== null && existing?.key !== undefined) {
      wipeBytes(existing.key);
    }
    this.states.set(k, { epoch, nonceSeq: 0, key: null });
  }

  async nextNonceSeq(conversationId: Uint8Array): Promise<{ epoch: number; nonceSeq: number }> {
    const k = this.keyFor(conversationId);
    let state = this.states.get(k);
    if (state === undefined) {
      state = { epoch: 0, nonceSeq: 0, key: null };
      this.states.set(k, state);
    }
    const nonceSeq = state.nonceSeq;
    state.nonceSeq += 1;
    return { epoch: state.epoch, nonceSeq };
  }

  async deriveKey(conversationId: Uint8Array): Promise<Uint8Array> {
    const k = this.keyFor(conversationId);
    let state = this.states.get(k);
    if (state === undefined) {
      state = { epoch: 0, nonceSeq: 0, key: null };
      this.states.set(k, state);
    }
    if (state.key !== null) {
      return state.key;
    }
    const secret = await this.exporter.exportConversationSecret(
      conversationId,
      EPHEMERAL_SECRET_LABEL,
      conversationId,
      EPHEMERAL_SECRET_LENGTH,
    );
    state.key = secret;
    return secret;
  }

  clear(): void {
    for (const state of this.states.values()) {
      if (state.key !== null) {
        wipeBytes(state.key);
      }
    }
    this.states.clear();
  }
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
